/**
 * ForwardPacketCard — primary MVP exit ramp.
 *
 * Renders three first-class actions:
 *   1. Email me a copy
 *   2. Forward to my loan officer
 *   3. Forward to my realtor
 *
 * Plus a fourth "Download PDF" affordance. Each forward generates the appropriate
 * packet (Recommendation v1 or Underwriting v2), POSTs it to the send-packet edge
 * function (which delivers via Resend and logs to lo_referrals), and shows a
 * confirmation. Used at end of recommendations and end of Owen's verdict.
 */
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Surface } from '../../ui/components';
import {
  Send, Mail, User, Briefcase, Download, CheckCircle, Loader2, Share2, X,
} from 'lucide-react';
import { calculateReadinessBreakdown } from '../../utils/readinessScoring';
import {
  buildRecommendationPacketBlob,
  buildUnderwritingPacketBlob,
  blobToBase64,
  downloadPacket,
} from '../../utils/packetGenerator.jsx';
import { supabase } from '../../lib/supabase';

const TIERS = [
  { min: 90, label: 'Outstanding' },
  { min: 70, label: 'Excellent' },
  { min: 50, label: 'Strong Profile' },
  { min: 30, label: 'Building Momentum' },
  { min: 15, label: 'On Your Way' },
  { min: 0,  label: 'Getting Started' },
];

const tierFor = (s) => (TIERS.find(t => s >= t.min) || TIERS[TIERS.length - 1]).label;

const RECIPIENT_LABELS = {
  self: { title: 'Email Me a Copy', desc: 'Send the packet to your inbox.', icon: Mail },
  loan_officer: { title: 'Forward to my Loan Officer', desc: 'Email it directly to your LO.', icon: Briefcase },
  realtor: { title: 'Forward to my Realtor', desc: 'Email it directly to your realtor.', icon: User },
};

const ForwardPacketCard = ({ packetVersion = 'recommendation', verdict = null, documentChecklist = null }) => {
  const state = useSelector(s => s.loanApplication);
  const {
    user,
    recommendedProduct,
    recommendedProducts,
    productRecommendation,
    applicationData,
    readinessScore,
    referrer,
  } = state;

  const [openModal, setOpenModal] = useState(null); // 'self' | 'loan_officer' | 'realtor' | null
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientCompany, setRecipientCompany] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [sentTo, setSentTo] = useState([]); // [{type, email}]
  const [downloading, setDownloading] = useState(false);

  const borrowerName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    [productRecommendation?.firstName, productRecommendation?.lastName].filter(Boolean).join(' ') ||
    '';

  const breakdown = calculateReadinessBreakdown(state);
  const scoreTier = tierFor(readinessScore);

  const packetProps = {
    borrowerName,
    recommendedProduct,
    recommendedProducts: recommendedProducts || [],
    readinessScore,
    scoreTier,
    scoreBreakdown: breakdown,
    productRecommendation,
    applicationData,
    verdict,
    documentChecklist,
  };

  const openFor = (type) => {
    setOpenModal(type);
    setError(null);
    if (type === 'self') {
      setRecipientName(borrowerName || '');
      setRecipientEmail(user?.email || '');
      setRecipientCompany('');
    } else {
      setRecipientName('');
      setRecipientEmail('');
      setRecipientCompany('');
    }
    setMessage('');
  };

  const closeModal = () => {
    if (submitting) return;
    setOpenModal(null);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadPacket(packetVersion, packetProps);
    } catch (e) {
      console.warn('Download failed:', e);
    } finally {
      setDownloading(false);
    }
  };

  const handleSend = async () => {
    if (!recipientEmail || !/^\S+@\S+\.\S+$/.test(recipientEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    setSubmitting(true);
    setError(null);

    try {
      const blob =
        packetVersion === 'underwriting'
          ? await buildUnderwritingPacketBlob(packetProps)
          : await buildRecommendationPacketBlob(packetProps);
      const pdfBase64 = await blobToBase64(blob);

      const payload = {
        applicationId: state?.currentApplicationId || null,
        borrowerUserId: user?.id || null,
        borrowerName,
        borrowerEmail: user?.email || null,
        recipientType: openModal,
        recipientEmail,
        recipientName: recipientName || null,
        recipientCompany: recipientCompany || null,
        packetVersion,
        pdfBase64,
        message: message || null,
        referralPartnerEmail: referrer?.email || null,
      };

      // Use Supabase Functions invoker if available; falls back to fetch.
      const { data, error: fnError } = await supabase.functions.invoke('send-packet', {
        body: payload,
      });

      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);

      setSentTo(prev => [...prev, { type: openModal, email: recipientEmail }]);
      setOpenModal(null);
    } catch (e) {
      console.error('send-packet failed:', e);
      setError('We couldn\'t send the email. Please try again or download the PDF instead.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderRecipientButton = (type) => {
    const cfg = RECIPIENT_LABELS[type];
    const Icon = cfg.icon;
    const alreadySent = sentTo.some(s => s.type === type);
    return (
      <button
        key={type}
        onClick={() => openFor(type)}
        style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: 14,
          border: alreadySent ? '1px solid var(--lk-success-300)' : '1px solid var(--lk-border)',
          borderRadius: 8, backgroundColor: alreadySent ? 'var(--lk-success-50)' : 'var(--lk-bg-surface-1)',
          textAlign: 'left', cursor: 'pointer', transition: 'all 0.15s ease',
          width: '100%',
        }}
      >
        <div style={{
          width: 36, height: 36, borderRadius: 8,
          backgroundColor: alreadySent ? 'var(--lk-success-100)' : 'var(--lk-primary-50)',
          color: alreadySent ? 'var(--lk-success-700)' : 'var(--lk-primary-600)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          {alreadySent ? <CheckCircle size={18} /> : <Icon size={18} />}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 'var(--lk-text-sm)', fontWeight: 600, color: 'var(--lk-text-primary)' }}>
            {cfg.title}
          </div>
          <div style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-tertiary)' }}>
            {alreadySent ? `Sent to ${sentTo.find(s => s.type === type).email}` : cfg.desc}
          </div>
        </div>
        <Send size={14} color="var(--lk-text-quaternary)" />
      </button>
    );
  };

  const heading = packetVersion === 'underwriting'
    ? 'Forward Your Pre-Underwritten File'
    : 'Forward Your Recommendation';

  const sub = packetVersion === 'underwriting'
    ? "Send the full file — application, Owen's findings, document checklist — to anyone who needs it. Your loan officer will love how clean it is."
    : "Email yourself a copy or send the recommendation packet directly to your loan officer or realtor.";

  return (
    <Surface padding="var(--lk-space-6)" style={{
      backgroundColor: 'var(--lk-primary-50)',
      border: '1px solid var(--lk-primary-200)',
      marginTop: 'var(--lk-space-6)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 'var(--lk-space-2)' }}>
        <Share2 size={18} color="var(--lk-primary-600)" />
        <h5 style={{ margin: 0, fontWeight: 700, fontSize: 'var(--lk-text-lg)' }}>{heading}</h5>
      </div>
      <p style={{
        fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-secondary)',
        marginBottom: 'var(--lk-space-4)',
      }}>
        {sub}
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 'var(--lk-space-3)' }}>
        {renderRecipientButton('self')}
        {renderRecipientButton('loan_officer')}
        {renderRecipientButton('realtor')}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="ghost"
          size="sm"
          icon={downloading ? Loader2 : Download}
          onClick={handleDownload}
          disabled={downloading}
        >
          {downloading ? 'Generating PDF…' : 'Download PDF instead'}
        </Button>
      </div>

      {/* Send modal */}
      {openModal && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed', inset: 0, zIndex: 1200,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={closeModal}
            style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)' }}
          />
          <div style={{
            position: 'relative', backgroundColor: 'var(--lk-bg-surface-1)',
            borderRadius: 12, width: '100%', maxWidth: 480,
            padding: 'var(--lk-space-6)', boxShadow: 'var(--lk-shadow-xl)',
            margin: 16,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--lk-space-4)' }}>
              <h5 style={{ margin: 0, fontWeight: 700 }}>
                {RECIPIENT_LABELS[openModal].title}
              </h5>
              <button
                onClick={closeModal}
                disabled={submitting}
                style={{
                  background: 'none', border: 'none', cursor: submitting ? 'wait' : 'pointer',
                  padding: 4, color: 'var(--lk-text-tertiary)',
                }}
              >
                <X size={18} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {openModal !== 'self' && (
                <>
                  <Field
                    label={openModal === 'loan_officer' ? 'Loan Officer Name' : 'Realtor Name'}
                    value={recipientName}
                    onChange={setRecipientName}
                    placeholder="Optional"
                  />
                  <Field
                    label="Company"
                    value={recipientCompany}
                    onChange={setRecipientCompany}
                    placeholder="Optional"
                  />
                </>
              )}
              <Field
                label="Email Address"
                type="email"
                value={recipientEmail}
                onChange={setRecipientEmail}
                placeholder="email@example.com"
                required
              />
              <Field
                label="Note (optional)"
                value={message}
                onChange={setMessage}
                multiline
                placeholder={
                  openModal === 'self'
                    ? 'Add a note to yourself…'
                    : `Hi ${recipientName || 'there'} — sharing my LoanKnow packet…`
                }
              />
              {error && (
                <div style={{
                  padding: 10, borderRadius: 6,
                  backgroundColor: 'var(--lk-danger-50)', color: 'var(--lk-danger-700)',
                  fontSize: 'var(--lk-text-sm)',
                }}>
                  {error}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 'var(--lk-space-5)' }}>
              <Button variant="ghost" onClick={closeModal} disabled={submitting}>
                Cancel
              </Button>
              <Button
                variant="primary"
                icon={submitting ? Loader2 : Send}
                onClick={handleSend}
                disabled={submitting || !recipientEmail}
              >
                {submitting ? 'Sending…' : 'Send Packet'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Surface>
  );
};

const Field = ({ label, value, onChange, placeholder, type = 'text', required, multiline }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    <span style={{ fontSize: 'var(--lk-text-xs)', fontWeight: 600, color: 'var(--lk-text-secondary)' }}>
      {label}{required ? ' *' : ''}
    </span>
    {multiline ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
        style={{
          padding: '10px 12px', border: '1px solid var(--lk-border)',
          borderRadius: 6, fontSize: 'var(--lk-text-sm)',
          backgroundColor: 'var(--lk-bg-surface-1)',
          fontFamily: 'inherit', resize: 'vertical', outline: 'none',
        }}
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          padding: '10px 12px', border: '1px solid var(--lk-border)',
          borderRadius: 6, fontSize: 'var(--lk-text-sm)',
          backgroundColor: 'var(--lk-bg-surface-1)',
          outline: 'none',
        }}
      />
    )}
  </label>
);

export default ForwardPacketCard;
